import React, {useState} from "react";
import {
    Avatar,
    Badge,
    Box,
    Button,
    ButtonGroup,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    Td,
    Text,
    Tooltip,
    Tr,
    useColorModeValue,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import {ArrowForwardIcon} from "@chakra-ui/icons";
import logo from "assets/icons/bell.png";
import BellPlayer from "../../views/Dashboard/Roster/Bells/BellPlayer";
import BellMusicSelect from "../BellMusicSelect/BellMusicSelect";
import {BASE_URL} from "../../constants/ApiConstant";
import {deleteBell, editBell} from "./services";
import {searchBellQueue} from "../../common-services";
import {setBellInfo} from "../../utils/BellStorageUtils";

// The backend stores the ring duration in seconds. When the admin enters minutes
// we convert to seconds before sending. `toSeconds` returns null for blanks so an
// untouched field keeps the existing value.
const toSeconds = (value, unit) => {
    if (value === "" || value == null) return null;
    const n = parseFloat(value);
    if (isNaN(n) || n < 0) return null;
    return unit === "min" ? Math.round(n * 60) : Math.round(n);
};

// Recommended quick-pick durations shown as chips in the edit modal.
const SAMPLE_SECONDS = [15, 30, 45, 60];
const SAMPLE_MINUTES = [1, 2, 5, 10];

function BelllTableRow(props) {
    const {id, name, hourTime, queue, bell,playTime, email, status, bellMusicId,bellName, fetchBellList,setIsEditSubmitted} = props;
    const textColor = useColorModeValue("gray.700", "white");
    const bgStatus = useColorModeValue("gray.400", "#1a202c");
    const colorStatus = useColorModeValue("white", "gray.400");

    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isChangeTimeOpen, setIsChangeTimeOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const handleEditOpen = () => setIsEditOpen(true);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [editData, setEditData] = useState({
        hourTime: "",
        // Prefill with the current play time from the search response so the
        // field shows the existing value instead of an empty box.
        playTime: playTime != null ? String(playTime) : "",
        status: status,
        bellMusicId: bellMusicId ?? null,
        bellMusicUrl: null,
    });
    // Unit for the play-time input — "sec" or "min". Backend always receives
    // seconds (converted on submit).
    const [playTimeUnit, setPlayTimeUnit] = useState("sec");
    const handleEditClose = () => setIsEditOpen(false);

    // Seconds equivalent of whatever the admin currently typed, for the live
    // preview and change detection.
    const playTimeSeconds = toSeconds(editData.playTime, playTimeUnit);

    // Switch unit and convert the displayed value so the real duration is kept.
    const handleUnitChange = (nextUnit) => {
        if (nextUnit === playTimeUnit) return;
        setEditData((prev) => {
            if (prev.playTime === "") return prev;
            const seconds = toSeconds(prev.playTime, playTimeUnit);
            if (seconds == null) return prev;
            const converted = nextUnit === "min"
                ? String(Math.round((seconds / 60) * 100) / 100)
                : String(seconds);
            return {...prev, playTime: converted};
        });
        setPlayTimeUnit(nextUnit);
    };

    // Apply a recommended sample chip (sets both the value and its unit).
    const applySample = (value, unit) => {
        setPlayTimeUnit(unit);
        setEditData((prev) => ({...prev, playTime: String(value)}));
    };

    const handleMusicChange = (item) => {
        setEditData((prev) => ({
            ...prev,
            bellMusicId: item?.value ?? item?.id ?? item?.bellMusicId ?? null,
            bellMusicUrl: item?.url ?? null,
        }));
    };

    const isMusicChanged =
        editData.bellMusicId != null && editData.bellMusicId !== bellMusicId;
    const isPlayTimeChanged =
        editData.playTime !== "" && playTimeSeconds !== (playTime ?? null);
    const hasEditChanges =
        !!editData.hourTime || isMusicChanged || isPlayTimeChanged || editData.status !== status;

    const handleChangeTimeOpen = () => setIsChangeTimeOpen(true);
    const handleChangeTimeClose = () => setIsChangeTimeOpen(false);

    const handleDeleteOpen = () => setIsDeleteOpen(true);
    const handleDeleteClose = () => setIsDeleteOpen(false);

    const updateLocalStorage = async () => {
        console.log("updating process started")
        try {
            const response = await searchBellQueue()
            if (response) {
                // force: re-cache audio even at an unchanged URL (bell replaced).
                setBellInfo(response.data, {force: true});
            }
        } catch (e) {
            console.error("Error while updating local storage e:", e);
        }

    }

    const handleDeleteConfirm = async (id) => {
        console.log("handleDeleteConfirm: {}", id);
        try {
            let request = {
                id: id
            }
            const response = await deleteBell(request);
            if (response.status === 200) {
                setIsDeleteOpen(false);
                await updateLocalStorage();
                fetchBellList();
            }
        } catch (e) {
            console.log(e);
        }

    }

    const handleEditChange = (e) => {
        const {name, value} = e.target;
        setEditData(prevState => ({...prevState, [name]: value}));
    };

    const handleEditConfirm = async (bellId) => {
        console.log("handleEditConfirm: {},{}", bellId);

        let request = {
            id: bellId,
            hourTime: editData.hourTime || hourTime,
            // How long the bell rings (seconds). The input may be in minutes, so
            // convert to seconds here. Keep the current value when untouched.
            playTime: editData.playTime !== "" ? playTimeSeconds : (playTime ?? null),
            // Send the newly chosen bell music (id + url) when the user changed it,
            // otherwise keep the current one so nothing is lost.
            bellMusicId: editData.bellMusicId ?? bellMusicId ?? null,
            fileUrl: editData.bellMusicUrl || bell,
            name: name,
            status: editData.status,
        }
        try {
            const response = await editBell(request);
            if (response.status === 200) {
                setIsEditOpen(false);
                setIsSubmitted(true);
                if (setIsEditSubmitted) {
                    setIsEditSubmitted(true);
                }
                fetchBellList();
                setEditData({
                    hourTime: "",
                    playTime: request.playTime != null ? String(request.playTime) : "",
                    status: status,
                    bellMusicId: bellMusicId ?? null,
                    bellMusicUrl: null,
                });
                // Reset the unit back to seconds now the saved value is in seconds.
                setPlayTimeUnit("sec");
                // Refresh the local bell queue so the in-app scheduler picks up
                // the new time / music immediately (and re-caches the R2 file).
                await updateLocalStorage();
            }
        } catch (e) {
            console.log(e);
        }

    }

    const cellStyle = {
        padding: "0.5px 4px", // Reduce padding
        fontSize: "14px", // Adjust font size
    };

    return (
        <>
            {/* Table Row */}
                <Tr style={{height: "60px"}}>
                    <Td style={cellStyle} pl="0px">
                        <Flex align="center" py="4px" minWidth="20%" flexWrap="nowrap">
                            <Avatar src={logo} w="40px" h="40px" borderRadius="12px" me="8px"/>
                            <Flex direction="column">
                                <Text fontSize="sm" color={textColor} fontWeight="bold" minWidth="100%">
                                    {name}
                                </Text>
                                <Text fontSize="xs" color="gray.400" fontWeight="normal">
                                    {email}
                                </Text>
                            </Flex>
                        </Flex>
                    </Td>

                    <Td style={cellStyle}>
                        <Text fontSize="sm" color={textColor} fontWeight="bold" pb="2px">
                            {hourTime}
                        </Text>
                    </Td>

                    <Td style={cellStyle}>
                        <Text fontSize="sm" color={textColor} fontWeight="bold" pb="2px">
                            {playTime}
                        </Text>
                    </Td>

                    <Td style={cellStyle}>
                        <BellPlayer bell={bell} bellName={bellName} audioSrc={BASE_URL.concat("")} textColor={"red"}/>
                    </Td>

                    <Td style={cellStyle}>
                        <Text fontSize="sm" color={textColor} fontWeight="bold" pb="2px">
                            {queue}
                        </Text>
                    </Td>

                    <Td style={cellStyle}>
                        <Badge
                            bg={status === "Y" ? "green.400" : bgStatus}
                            color={status === "Y" ? "white" : colorStatus}
                            fontSize="12px"
                            p="2px 6px"
                            borderRadius="8px">
                            {status === "Y" ? "सक्रिय" : "निष्क्रिय"}
                        </Badge>
                    </Td>

                    <Td style={cellStyle}>
                        <ButtonGroup size="sm" variant="ghost" spacing="2">
                            <Tooltip label="Edit" aria-label="Edit Tooltip">
                                <Button onClick={handleEditOpen}>Edit</Button>
                            </Tooltip>
                            <Tooltip label="Delete" aria-label="Delete Tooltip">
                                <Button colorScheme="red" onClick={handleDeleteOpen}>
                                    Delete
                                </Button>
                            </Tooltip>
                            {/*<Tooltip label="Change Time" aria-label="Change Time Tooltip">*/}
                            {/*    <Button colorScheme="blue" onClick={handleChangeTimeOpen}>*/}
                            {/*        Change Time*/}
                            {/*    </Button>*/}
                            {/*</Tooltip>*/}
                        </ButtonGroup>
                    </Td>
                </Tr>

            {/* Edit Modal */}
                <Modal isOpen={isEditOpen} onClose={handleEditClose}>
                    <ModalOverlay/>
                    <ModalContent>
                        <ModalHeader>घण्टी विवरण सम्पादन (Edit)</ModalHeader>
                        <ModalCloseButton/>
                        <ModalBody>

                            <FormControl>
                                <FormLabel>Name</FormLabel>
                                <Input name={"name"} defaultValue={name} isDisabled/>
                            </FormControl>

                            {/* Interactive previous → new time comparison. */}
                            <FormControl mt={4}>
                                <FormLabel>घण्टी समय (अघिल्लो → नयाँ)</FormLabel>
                                <HStack align="center" spacing={3}>
                                    <Box flex="1">
                                        <Text fontSize="xs" color="gray.400" mb={1}>अघिल्लो</Text>
                                        <Badge
                                            w="100%"
                                            textAlign="center"
                                            py={2}
                                            borderRadius="8px"
                                            colorScheme="gray"
                                            fontSize="sm"
                                        >
                                            {hourTime || "—"}
                                        </Badge>
                                    </Box>
                                    <ArrowForwardIcon boxSize={5} color="blue.400" mt={5}/>
                                    <Box flex="1">
                                        <Text fontSize="xs" color="gray.400" mb={1}>नयाँ</Text>
                                        <Input
                                            type="time"
                                            name="hourTime"
                                            value={editData.hourTime}
                                            onChange={(e) => handleEditChange(e)}
                                            borderColor={editData.hourTime ? "blue.300" : undefined}
                                        />
                                    </Box>
                                </HStack>
                                <FormHelperText>
                                    {editData.hourTime
                                        ? `समय ${hourTime || "—"} बाट ${editData.hourTime} मा परिवर्तन हुनेछ।`
                                        : "नयाँ समय नछनौट गरेमा अघिल्लो समय यथावत रहनेछ।"}
                                </FormHelperText>
                            </FormControl>

                            {/* Play duration with a seconds/minutes toggle + recommended
                                samples. Value is converted to seconds on submit. */}
                            <FormControl mt={4}>
                                <FormLabel>बजाउने समय (अवधि)</FormLabel>
                                <HStack spacing={2} align="stretch">
                                    <Input
                                        type="number"
                                        min="0"
                                        step={playTimeUnit === "min" ? "0.5" : "1"}
                                        name="playTime"
                                        value={editData.playTime}
                                        placeholder={playTime != null ? String(playTime) : "जस्तै, ६०"}
                                        onChange={(e) => handleEditChange(e)}
                                        flex="1"
                                    />
                                    <ButtonGroup isAttached size="md" variant="outline">
                                        <Button
                                            colorScheme={playTimeUnit === "sec" ? "blue" : "gray"}
                                            variant={playTimeUnit === "sec" ? "solid" : "outline"}
                                            onClick={() => handleUnitChange("sec")}
                                        >
                                            सेकेन्ड
                                        </Button>
                                        <Button
                                            colorScheme={playTimeUnit === "min" ? "blue" : "gray"}
                                            variant={playTimeUnit === "min" ? "solid" : "outline"}
                                            onClick={() => handleUnitChange("min")}
                                        >
                                            मिनेट
                                        </Button>
                                    </ButtonGroup>
                                </HStack>

                                {/* Recommended sample chips. */}
                                <Text fontSize="xs" color="gray.400" mt={3} mb={1}>सिफारिस (सेकेन्ड)</Text>
                                <Wrap spacing={2}>
                                    {SAMPLE_SECONDS.map((sec) => (
                                        <WrapItem key={`s-${sec}`}>
                                            <Button
                                                size="xs"
                                                variant={playTimeUnit === "sec" && String(sec) === String(editData.playTime) ? "solid" : "outline"}
                                                colorScheme="blue"
                                                borderRadius="full"
                                                onClick={() => applySample(sec, "sec")}
                                            >
                                                {sec}s
                                            </Button>
                                        </WrapItem>
                                    ))}
                                </Wrap>
                                <Text fontSize="xs" color="gray.400" mt={2} mb={1}>सिफारिस (मिनेट)</Text>
                                <Wrap spacing={2}>
                                    {SAMPLE_MINUTES.map((min) => (
                                        <WrapItem key={`m-${min}`}>
                                            <Button
                                                size="xs"
                                                variant={playTimeUnit === "min" && String(min) === String(editData.playTime) ? "solid" : "outline"}
                                                colorScheme="purple"
                                                borderRadius="full"
                                                onClick={() => applySample(min, "min")}
                                            >
                                                {min} min
                                            </Button>
                                        </WrapItem>
                                    ))}
                                </Wrap>

                                <FormHelperText>
                                    {playTimeSeconds != null
                                        ? `बैकएन्डमा ${playTimeSeconds} सेकेन्ड पठाइनेछ${playTimeUnit === "min" ? ` (${editData.playTime} मिनेट)` : ""}।`
                                        : "एउटा अवधि प्रविष्ट गर्नुहोस् वा माथिबाट सिफारिस छान्नुहोस्।"}
                                </FormHelperText>
                            </FormControl>

                            <FormControl mt={4}>
                                <FormLabel>बेल संगीत</FormLabel>
                                <BellMusicSelect
                                    value={editData.bellMusicId}
                                    onChange={handleMusicChange}
                                    placeholder="बेल संगीत चयन गर्नुहोस्"
                                />
                            </FormControl>

                            <FormControl mt={4}>
                                <FormLabel>Status</FormLabel>
                                <Select defaultValue={status} onClick={handleEditChange} name={"status"}>
                                    <option value="Y">Active</option>
                                    <option value="N">Inactive</option>
                                </Select>
                            </FormControl>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                colorScheme="blue"
                                mr={3}
                                onClick={() => handleEditConfirm(id)}
                                isDisabled={!hasEditChanges}
                            >
                                Save
                            </Button>
                            <Button variant="ghost" onClick={handleEditClose}>
                                Cancel
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>


                {/* Change Time Modal */}
                <Modal isOpen={isChangeTimeOpen} onClose={handleChangeTimeClose}>
                    <ModalOverlay/>
                    <ModalContent>
                        <ModalHeader>Change Bell Time</ModalHeader>
                        <ModalCloseButton/>
                        <ModalBody>
                            <FormControl>
                                <FormLabel>Hour</FormLabel>
                                <NumberInput defaultValue={parseInt(hourTime.split(":")[0])} min={0} max={23}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                            <FormControl mt={4}>
                                <FormLabel>Minute</FormLabel>
                                <NumberInput defaultValue={parseInt(hourTime.split(":")[1])} min={0} max={59}>
                                    <NumberInputField/>
                                    <NumberInputStepper>
                                        <NumberIncrementStepper/>
                                        <NumberDecrementStepper/>
                                    </NumberInputStepper>
                                </NumberInput>
                            </FormControl>
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={handleChangeTimeClose}>
                                Save
                            </Button>
                            <Button variant="ghost" onClick={handleChangeTimeClose}>
                                Cancel
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={isDeleteOpen} onClose={handleDeleteClose}>
                    <ModalOverlay/>
                    <ModalContent>
                        <ModalHeader>Confirm Delete</ModalHeader>
                        <ModalCloseButton/>
                        <ModalBody>
                            Are you sure you want to delete this bell record?
                        </ModalBody>
                        <ModalFooter>
                            <Button colorScheme="red" mr={3} onClick={() => handleDeleteConfirm(id)}>
                                Delete
                            </Button>
                            <Button variant="ghost" onClick={handleDeleteClose}>
                                Cancel
                            </Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>
        </>
    );
}

export default BelllTableRow;
